import React from 'react';
import type { FC, PropsWithChildren } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import type { LinkProps } from 'react-router-dom';
import { useBuildURL } from '../../../../../../hooks/useBuildURL';
import { Text } from '../../../../../../ui-components';
import { sanitizeHtml } from '../../../../../content-asset/sanitize-html';
import { SoftLink } from '../../../../../soft-link/SoftLink';
import { SuggestionsProductImage } from './SuggestionsProductImage';
import type { BloomreachProductType } from '../../../../../../bff-api/bloomreach-recommendation/model/bloomreach';
import type { Products } from '../../../../../../bff-api/products/model';

type LinkWrapperProps = Omit<LinkProps, 'to'>;

const LinkWrapper: FC<
    PropsWithChildren<
        {
            to: string;
            isLocal: boolean;
        } & LinkWrapperProps
    >
> = ({ to, isLocal, children, ...rest }) => {
    if (isLocal) {
        return (
            <SoftLink to={to} {...rest} showLoader>
                {children}
            </SoftLink>
        );
    }
    return (
        <a href={to} {...rest}>
            {children}
        </a>
    );
};

type Props = {
    products: Products;
    currency: string;
    onClick: (searchSuggestion: string) => void;
    type: BloomreachProductType;
};

export const MenuSearchProducts: FC<Props> = ({ products, type, currency, onClick }) => {
    const { buildProductURL } = useBuildURL();

    return (
        <Flex rowGap={6} columnGap={6} direction={{ base: 'column', md: 'row' }} wrap="wrap">
            {products.map(({ id, name, price, c_pwaUrl, c_imageUrl }) => {
                const isSale =
                    price && (price.toString().endsWith('9') || price.toString().endsWith('6'));

                return (
                    <LinkWrapper
                        key={id}
                        data-testid={`header.menu.search.item.${id}`}
                        to={buildProductURL(id, type, c_pwaUrl)}
                        isLocal={type === 'hardgood'}
                        onClick={(): void => onClick(id)}
                        className="align-center flex gap-3 md:w-[162px] md:flex-col lg:w-[225px] xl:w-[304px]"
                    >
                        <Box maxWidth={{ base: 138, md: 'unset' }}>
                            <SuggestionsProductImage pid={id} name={name!} imageUrl={c_imageUrl!} />
                        </Box>

                        <Flex direction="column" gap={2}>
                            <Text
                                id={`header.menu.search.item.${id}.name`}
                                size="small"
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeHtml(name),
                                }}
                            />

                            {price ? (
                                <Text
                                    id={`header.menu.search.item.${id}.price`}
                                    style="bold"
                                    size="small"
                                    color={isSale ? 'slt-red-error' : undefined}
                                >
                                    <FormattedMessage
                                        id="header.menu.search.from"
                                        defaultMessage="From"
                                    />
                                    {': '}
                                    <FormattedNumber
                                        style="currency"
                                        currency={currency}
                                        value={price}
                                    />
                                </Text>
                            ) : null}
                        </Flex>
                    </LinkWrapper>
                );
            })}
        </Flex>
    );
};
